import useAxios from "../../../../../hooks/useAxios";
import { useEffect, useRef, useState } from "react";
import {
  CLIENT_PLATFORM_MARGINS_CHANGELOG,
  TOOLS,
} from "../../../../../shared/lib/routesResolver";
import {
  API_CLIENT_PLATFORM_MARGINS,
  API_CLIENT_PLATFORM_MARGINS_BY_ID,
  API_CLIENT_PLATFORM_MARGINS_CHANGELOG,
  API_CLIENTS_SELECTABLE,
  API_PLATFORMS_SELECTABLE,
} from "../../../../../shared/lib/apiEndpointsResolver";
import { catchFunction } from "../../../../../shared/lib/utils";
import { useTracking } from "react-tracking";
import { useTranslation } from "react-i18next";
import OverviewTabContent from "../../../../../shared/components/overview-panel/OverviewTabContent";
import OverviewComponent from "../../../../../shared/components/overview-panel/OverviewComponent";
import ClientPlatformMarginsTable from "./ClientPlatformMarginsTable";
import CreateClientPlatformMarginDialog from "./create/CreateClientPlatformMarginDialog";
import useLoading from "../../../../../shared/components/loading/useLoading";
import useToastWrapper from "../../../../../hooks/useToastWrapper";
import useConfirmDialog from "../../../../../shared/components/confirm-dialog/useConfirmDialog";
import UpdateClientPlatformMarginsDialog from "./update/UpdateClientPlatformMarginsDialog";
import DoubleLabelSwitch from "../../../../../shared/components/switch/DoubleLabelSwitch";
import ClientPlatformMarginsChangelogDialog from "./changelog/ClientPlatformMarginsChangelogDialog";
import { Button } from "../../../../../shared/components/button/Button";
import { useNavigate } from "react-router";

const ClientPlatformMarginsOverview = () => {
  const axios = useAxios();
  const { setLoading } = useLoading();
  const [clients, setClients] = useState([]);
  const [platforms, setPlatforms] = useState([]);
  const [records, setRecords] = useState([]);
  const recordsRef = useRef([]);
  const { trackEvent } = useTracking();
  const { t } = useTranslation();
  const toast = useToastWrapper();
  const deleteClientPlatformMarginDialog = useConfirmDialog();
  const [clientPlatformMarginForDeletion, setClientPlatformMarginForDeletion] =
    useState();
  const [externalOpen, setExternalOpen] = useState({ externalOpen: false });
  const [selectedRecord, setSelectedRecord] = useState();
  const [externalOpenChangelog, setExternalOpenChangelog] = useState({
    externalOpen: false,
  });
  const [recordChangelog, setRecordChangelog] = useState([]);
  const [recordId, setRecordId] = useState();
  const navigate = useNavigate();

  const breadcrumbItems = [
    {
      title: "clientPlatformMargins.overview.breadcrumb.first",
      link: TOOLS,
    },
    { title: "clientPlatformMargins.overview.breadcrumb.second" },
  ];

  const findRecords = () => {
    axios
      .get(API_CLIENT_PLATFORM_MARGINS)
      .then((response) => {
        setRecords(response.data);
        recordsRef.current = response.data;
      })
      .catch(catchFunction);
  };

  const findClients = () => {
    axios
      .get(API_CLIENTS_SELECTABLE)
      .then((response) => setClients(response.data))
      .catch(catchFunction);
  };

  const findPlatforms = () => {
    axios
      .get(API_PLATFORMS_SELECTABLE)
      .then((response) => setPlatforms(response.data))
      .catch(catchFunction);
  };

  const onClientPlatformMarginCreation = (formValues) => {
    setLoading(true);
    axios
      .post(API_CLIENT_PLATFORM_MARGINS, formValues)
      .then((response) => {
        const data = response.data;
        toast.success(
          t("clientPlatformMargins.createForm.success", {
            client: data.clients[0].client.name,
            platform: data.platform.name,
          }),
        );
        findRecords();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const deleteClientPlatformMargin = () => {
    setLoading(true);
    axios
      .delete(
        API_CLIENT_PLATFORM_MARGINS_BY_ID(clientPlatformMarginForDeletion.id),
      )
      .then((_) => {
        toast.success(t("clientPlatformMargins.deletionSuccessMessage"));
        findRecords();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onDeleteClientPlatformMargin = (id, platformId) => {
    const record = recordsRef.current.filter(
      (record) => record.platform.id === platformId,
    )[0];
    const clientPlatformMargin = record.clients.filter((x) => x.id === id)[0];
    setClientPlatformMarginForDeletion(clientPlatformMargin);
    deleteClientPlatformMarginDialog.display(
      `margin for ${clientPlatformMargin.client.name} at ${record.platform.name}`,
    );
  };

  const editClientPlatformMargin = (formValues) => {
    setLoading(true);
    axios
      .put(
        API_CLIENT_PLATFORM_MARGINS_BY_ID(
          selectedRecord.clientPlatformMargin.id,
        ),
        formValues,
      )
      .then((response) => {
        const data = response.data;
        toast.success("clientPlatformMargins.updateForm.success", {
          client: selectedRecord.clientPlatformMargin.client.name,
          platform: selectedRecord.platform.name,
        });
        findRecords();
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const onEditClientPlatformMargin = (clientPlatformMargin, platform) => {
    setExternalOpen({ ...externalOpen, externalOpen: true });
    setSelectedRecord({
      platform: platform,
      clientPlatformMargin: clientPlatformMargin,
    });
  };

  const onClientPlatformMarginFilter = (displayAllRecords) => {
    if (displayAllRecords === null || !displayAllRecords) {
      const platformsWithOnlyActiveClients = recordsRef.current
        .map((platform) => ({
          ...platform,
          clients: platform.clients.filter((client) => client.active === true),
        }))
        .filter((platform) => platform.clients.length > 0);

      setRecords(platformsWithOnlyActiveClients);
    } else {
      setRecords(recordsRef.current);
    }
  };

  const onChangelogDisplay = (id) => {
    setLoading(true);
    axios
      .get(API_CLIENT_PLATFORM_MARGINS_CHANGELOG(id))
      .then((response) => {
        setExternalOpenChangelog({
          ...externalOpenChangelog,
          externalOpen: true,
        });
        setRecordChangelog(response.data);
        setRecordId(id);
      })
      .catch(catchFunction)
      .finally(() => setLoading(false));
  };

  const OverviewOptions = (
    <div className="flex flex-row gap-x-4 items-center">
      <DoubleLabelSwitch
        leftLabel={t("defaults.activeLabel")}
        rightLabel={t("defaults.allTab")}
        defaultChecked={true}
        onCheckedChange={onClientPlatformMarginFilter}
      />
      <Button
        className="h-7 gap-1"
        onClick={() => navigate(CLIENT_PLATFORM_MARGINS_CHANGELOG)}
      >
        {t("clientPlatformMargins.overview.changelog")}
      </Button>
      <CreateClientPlatformMarginDialog
        onSubmitHandler={onClientPlatformMarginCreation}
        clients={clients}
        platforms={platforms}
      />
    </div>
  );

  const platformTabs = records.map((record, index) => {
    return {
      trigger: {
        value: `record_${index}`,
        label: record.platform.name,
      },
      content: (
        <OverviewTabContent
          value={`record_${index}`}
          title="clientPlatformMargins.overview.tabs.title"
          titleOptions={{ platform: record.platform.name }}
        >
          <ClientPlatformMarginsTable
            data={record}
            onEdit={onEditClientPlatformMargin}
            onDelete={onDeleteClientPlatformMargin}
            onChangelogDisplay={onChangelogDisplay}
          />
        </OverviewTabContent>
      ),
    };
  });

  useEffect(() => {
    findRecords();
    findClients();
    findPlatforms();
    trackEvent({
      page: "client-platform-margins-overview",
      event: "page-load",
    });
  }, []);

  return (
    <>
      {deleteClientPlatformMarginDialog.renderSafeDialog(
        deleteClientPlatformMargin,
      )}
      <ClientPlatformMarginsChangelogDialog
        id={recordId}
        records={recordChangelog}
        externalOpen={externalOpenChangelog}
        hiddenClassName="hidden"
      />
      <UpdateClientPlatformMarginsDialog
        clients={clients}
        platforms={platforms}
        clientPlatformMargin={selectedRecord?.clientPlatformMargin}
        onSubmitHandler={editClientPlatformMargin}
        externalOpen={externalOpen}
        hiddenClassName="hidden"
        platform={selectedRecord?.platform}
      />
      <OverviewComponent
        breadcrumbItems={breadcrumbItems}
        tabs={platformTabs}
        defaultTabValue={"record_0"}
        options={OverviewOptions}
      />
    </>
  );
};

export default ClientPlatformMarginsOverview;
